/**
 * The on-device record of finished focus blocks.
 *
 * The browser's copy is what the page reads, so it stays instant and works
 * offline; `lib/focus/focusSync` mirrors it to the backend's focus log and
 * merges back blocks finished on other devices.
 *
 * Kept apart from `useActivityTracking` on purpose: that feeds study hours and
 * the "meaningful session" streak on the backend, and a focus block wraps the
 * same lesson time those rows already record, so sending it there would count
 * it twice.
 */
import type { FocusBlock } from '@/lib/focus/focusMachine';

export interface FocusLogEntry {
  id: string;
  startedAt: number;
  focusMs: number;
  /** Absent on entries written before the server sync existed. */
  endedAt?: number;
  endedEarly?: boolean;
  lessonId?: string;
  intention?: string;
  recap?: string;
  /** The server holds this entry exactly as it is now. */
  synced?: boolean;
  /** The server refused it as malformed; kept here, never retried. */
  rejected?: boolean;
}

export const LOG_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Add (or refresh) the entry for `block`, dropping anything past retention.
 * A refreshed entry is unsynced again, so a recap added during the break is
 * sent up too.
 */
export function recordBlock(
  log: FocusLogEntry[],
  block: FocusBlock,
  now: number
): FocusLogEntry[] {
  const entry: FocusLogEntry = {
    id: block.id,
    startedAt: block.startedAt,
    focusMs: block.focusMs,
    endedAt: block.endedAt,
    endedEarly: block.endedEarly,
    lessonId: block.lessonId,
    intention: block.intention,
    recap: block.recap,
  };
  const kept = log.filter(
    e => e.id !== block.id && now - e.startedAt < LOG_RETENTION_MS
  );
  return [...kept, entry];
}

/** True when `block` is already in the log exactly as it is now. */
export function isRecorded(log: FocusLogEntry[], block: FocusBlock): boolean {
  return log.some(e => e.id === block.id && e.recap === block.recap);
}

function isSameLocalDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export interface DaySummary {
  blocks: number;
  focusMs: number;
}

/** Blocks and focus time started on the same local day as `now`. */
export function summarizeDay(log: FocusLogEntry[], now: number): DaySummary {
  return log
    .filter(e => isSameLocalDay(e.startedAt, now))
    .reduce<DaySummary>(
      (sum, e) => ({
        blocks: sum.blocks + 1,
        focusMs: sum.focusMs + e.focusMs,
      }),
      { blocks: 0, focusMs: 0 }
    );
}
